import React, { useEffect, useMemo, useRef } from 'react';

/**
 * The radial network behind the sign-in cover.
 *
 * Ported from the Biomed app shell at
 *   D:\cowork_project\information\src\app-shell.html  (`.c-net`, `#home`)
 * which is the source of the look this is meant to match. Kept faithful to it:
 * same geometry constants, same weights, same four animation periods, same
 * pointer behaviour. Class names carry an `lg-` prefix so nothing can collide
 * with dashboard.css or the frozen PDF report classes.
 *
 * Every spoke leaves the same centre and lands on a rim dot, bent by a fixed
 * angular offset — that constant offset is what makes the fan read as one
 * turning motion rather than a starburst. The composition is deterministic:
 * weights repeat on a short cycle so the figure is identical on every load.
 *
 * Motion is four loops at four periods with no common multiple, so it never
 * returns to the same arrangement. All of it is CSS on transform / opacity /
 * stroke-dashoffset — the only JavaScript that runs per frame is the pointer
 * spotlight, and only while the mouse is actually moving.
 */

const NET = { n: 128, R: 290, cx: 450, cy: 350, w: 900, h: 700, sweep: 0.42, bend: 0.52 };

// A prime step over a non-multiple count walks the whole rim without repeating
// a position, so the pulses spread evenly with nothing random deciding it.
const PULSE_STEP = 9;
const PULSE_COUNT = 14;

// Each label is something this product actually is, not filler.
const NET_LABELS = [
  [-72, 'MICROBIOME...'], [-14, 'PROBIOTICS...'], [44, 'GUT HEALTH...'],
  [126, 'LAB PANELS...'], [196, 'REPORTS...'],
];

const LoginNetworkBackground = () => {
  const netRef = useRef(null);

  const { spokes, bright, dots, pulses, inner, labels } = useMemo(() => {
    const { n, R, cx, cy, sweep, bend } = NET;
    const pt = (a, r) => [cx + Math.cos(a) * r, cy + Math.sin(a) * r];
    const ang = (i) => (i / n) * Math.PI * 2 - Math.PI / 2;
    // One spoke's curve, reused for the line and for the pulse that rides it.
    const spokeD = (i) => {
      const a = ang(i);
      const [ex, ey] = pt(a, R);
      const [qx, qy] = pt(a - sweep, R * bend);
      return `M${cx} ${cy}Q${qx.toFixed(1)} ${qy.toFixed(1)} ${ex.toFixed(1)} ${ey.toFixed(1)}`;
    };

    const sp = [], br = [], dt = [], pl = [], inr = [], lb = [];

    for (let i = 0; i < n; i++) {
      const [ex, ey] = pt(ang(i), R);
      // Three weights on a 7/3 cycle: most spokes are a whisper, some carry,
      // and a few take the accent teal so the fan has depth without a second
      // colour.
      const strong = i % 7 === 0;
      const accent = i % 23 === 0;
      const d = spokeD(i);

      sp.push(
        <path key={`s${i}`} d={d} fill="none"
          stroke={accent ? '#4797a8' : '#d3e0ea'}
          strokeOpacity={accent ? 0.55 : strong ? 0.34 : 0.15}
          strokeWidth={accent ? 0.9 : 0.7} />
      );

      // The same fan again, brighter. It is masked to a soft disc that follows
      // the cursor, so only the part under the pointer is ever visible — one
      // mask moving instead of 128 strokes being restyled every frame.
      br.push(
        <path key={`b${i}`} d={d} fill="none" stroke="#e2f4f9" strokeOpacity="0.95"
          strokeWidth={accent ? 1.6 : 1.25} />
      );
      br.push(
        <circle key={`bc${i}`} cx={ex.toFixed(1)} cy={ey.toFixed(1)}
          r={strong ? 3 : 2.4} fill="#ffffff" />
      );

      // Only the strong dots twinkle — animating opacity on all 128 would be a
      // lot of work for something nobody would read as busier.
      dt.push(
        <circle
          key={`d${i}`}
          className={strong ? 'lg-twk' : undefined}
          style={strong ? {
            '--d': `${(3.4 + (i % 5) * 0.7).toFixed(1)}s`,
            '--t': `-${((i * 0.31) % 4).toFixed(2)}s`,
          } : undefined}
          cx={ex.toFixed(1)} cy={ey.toFixed(1)}
          r={strong ? 2.1 : 1.6}
          fill="#d3e0ea" fillOpacity={strong ? 0.85 : 0.5} />
      );
    }

    // A short dash riding each chosen spoke from the core to the rim.
    // pathLength="100" normalises every curve to the same scale, so one dash
    // length and one keyframe pair work for all of them.
    for (let k = 0; k < PULSE_COUNT; k++) {
      const i = (k * PULSE_STEP) % n;
      pl.push(
        <path key={`p${k}`} className="lg-pulse" pathLength="100" d={spokeD(i)}
          fill="none" stroke="#eaf6fa" strokeWidth="1.3" strokeLinecap="round"
          style={{
            '--d': `${(4.2 + (k % 6) * 0.9).toFixed(1)}s`,
            '--t': `-${(k * 0.62).toFixed(2)}s`,
          }} />
      );
    }

    // A sparse inner ring turning the other way. Two speeds in opposite
    // directions is what stops the whole thing reading as one rigid wheel.
    for (let j = 0; j < 44; j++) {
      const a = (j / 44) * Math.PI * 2;
      const [ix, iy] = pt(a, R * 0.6);
      inr.push(
        <circle key={`i${j}`} cx={ix.toFixed(1)} cy={iy.toFixed(1)} r="1.3"
          fill="#7c87bf" fillOpacity="0.38" />
      );
    }

    for (const [deg, text] of NET_LABELS) {
      const a = (deg * Math.PI) / 180;
      const [lx, ly] = pt(a, R + 30);
      const anchor = Math.cos(a) < -0.2 ? 'end' : Math.cos(a) > 0.2 ? 'start' : 'middle';
      lb.push(
        <text key={text} x={lx.toFixed(1)} y={(ly + 3).toFixed(1)} textAnchor={anchor}
          fontSize="8.5" letterSpacing="1.6" fill="#d3e0ea" fillOpacity="0.62"
          fontFamily="ui-monospace, Consolas, monospace">{text}</text>
      );
    }

    return { spokes: sp, bright: br, dots: dt, pulses: pl, inner: inr, labels: lb };
  }, []);

  // ---- pointer response ----
  // Two things follow the cursor: the bright patch, exactly, so it feels stuck
  // to the pointer; and the whole figure, a few pixels and late, which is what
  // reads as depth. The lag is a CSS transition rather than a lerp loop, so
  // nothing runs when the mouse is still.
  useEffect(() => {
    const netEl = netRef.current;
    if (!netEl) return undefined;

    // Fine pointers only. On a touch screen there is no hover, and a spotlight
    // that appears wherever the last tap landed is just a smudge.
    const fine = window.matchMedia('(pointer: fine)').matches;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!fine || still) return undefined;

    const svg = netEl.querySelector('svg');
    const grad = svg && svg.querySelector('#lg-spotgrad');
    if (!svg || !grad) return undefined;

    const sp = svg.createSVGPoint();
    let px = 0, py = 0, raf = 0;

    const apply = () => {
      raf = 0;
      const m = svg.getScreenCTM();
      if (!m) return;
      sp.x = px; sp.y = py;
      const p = sp.matrixTransform(m.inverse());
      grad.setAttribute('cx', p.x.toFixed(1));
      grad.setAttribute('cy', p.y.toFixed(1));
      // Parallax is read off the viewport, not the circle, so the drift is the
      // same wherever the figure happens to sit.
      netEl.style.setProperty('--px', `${((px / window.innerWidth - 0.5) * -18).toFixed(1)}px`);
      netEl.style.setProperty('--py', `${((py / window.innerHeight - 0.5) * -12).toFixed(1)}px`);
    };

    const onMove = (e) => {
      if (e.pointerType !== 'mouse') return;
      px = e.clientX; py = e.clientY;
      netEl.classList.add('is-live');
      if (!raf) raf = window.requestAnimationFrame(apply);
    };

    // Leaving the window puts it back to rest rather than freezing the patch
    // wherever the pointer happened to exit.
    const onOut = (e) => {
      if (e.relatedTarget) return;
      netEl.classList.remove('is-live');
      netEl.style.setProperty('--px', '0px');
      netEl.style.setProperty('--py', '0px');
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerout', onOut, { passive: true });

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerout', onOut);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  const { cx, cy, w, h } = NET;

  return (
    <div className="lg-net" ref={netRef} aria-hidden="true">
      {/* The mask sits on the OUTER group and the rotation on the inner one.
          The other way round, the mask would turn with the fan and the bright
          patch would orbit the centre instead of staying under the cursor. */}
      <svg viewBox={`0 0 ${w} ${h}`} role="presentation" style={{ '--ox': `${cx}px`, '--oy': `${cy}px` }}>
        <defs>
          <radialGradient id="lg-coregrad">
            <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
            <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="lg-spotgrad" gradientUnits="userSpaceOnUse" cx={cx} cy={cy} r="150">
            <stop offset="0" stopColor="#fff" stopOpacity="1" />
            <stop offset="0.38" stopColor="#fff" stopOpacity="0.72" />
            <stop offset="1" stopColor="#fff" stopOpacity="0" />
          </radialGradient>
          <mask id="lg-spot" maskUnits="userSpaceOnUse" x="0" y="0" width={w} height={h}>
            <rect x="0" y="0" width={w} height={h} fill="url(#lg-spotgrad)" />
          </mask>
        </defs>

        <g className="lg-spin-r">{inner}</g>
        <g className="lg-spin">{spokes}{dots}{pulses}</g>
        <g className="lg-glow" mask="url(#lg-spot)"><g className="lg-spin">{bright}</g></g>

        {/* The core and the labels sit outside the spinning groups, because a
            label that rotates ends up upside down. */}
        <circle className="lg-core" cx={cx} cy={cy} r="34" fill="url(#lg-coregrad)" />
        <circle cx={cx} cy={cy} r="2.6" fill="#ffffff" />
        {labels}
      </svg>
    </div>
  );
};

export default LoginNetworkBackground;
